import { useState } from 'react';
import SEO from '../../../components/SEO';

export default function NewVsRenovationPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://buyinzagreb.com';

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Novogradnja ili adaptacija starogradnje? Vodič za kupce",
    "description": "Detaljno poređenje prednosti i nedostataka kupnje novogradnje u odnosu na adaptaciju starije nekretnine u Zagrebu.",
    "image": "https://public.readdy.ai/ai/img_res/34bb22ed31db08a8191e8b6f10e27c5c.jpg",
    "author": {
      "@type": "Organization",
      "name": "BuyInZagreb Stručni tim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BuyInZagreb",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": "2024-01-25",
    "dateModified": "2024-01-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/novogradnja-ili-adaptacija-zagreb`
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/blog-new-vs-renovation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Novogradnja ili adaptacija Zagreb? Vodič za kupce 2024"
        description="Detaljno poređenje novogradnje i adaptacije starogradnje Zagreb. Troškovi, prednosti, nedostaci i savjeti za kupce nekretnina. Agencije za nekretnine Zagreb."
        keywords="novogradnja Zagreb, adaptacija, stanovi Zagreb, renovacija, agencije za nekretnine Zagreb, kupnja nekretnine"
        canonical={`${siteUrl}/blog/novogradnja-ili-adaptacija-zagreb`}
        schemaData={articleSchema}
      />

      {/* Header - Simple */}
      <header className="fixed top-0 w-full z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                alt="BuyInZagreb Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            <nav className="hidden lg:flex items-center space-x-8">
              {[
                { name: 'Početna', href: '/' },
                { name: 'Nekretnine', href: '/#listings' },
                { name: 'Usluge', href: '/#services' },
                { name: 'O nama', href: '/#about' },
                { name: 'Blog', href: '/#blog-tools' },
                { name: 'Kontakt', href: '/#contact' }
              ].map((item, index) => (
                <a 
                  key={item.name}
                  href={item.href} 
                  className="text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 cursor-pointer"
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <div className="flex items-center space-x-4">
              <a 
                href="/#contact"
                className="bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Kontaktirajte nas
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Article Header */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Novogradnja ili adaptacija starogradnje? Vodič za kupce
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Detaljno poređenje prednosti i nedostataka kupnje novogradnje u odnosu na adaptaciju starije nekretnine u Zagrebu.
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span><i className="ri-calendar-line mr-2"></i>25. siječnja 2024.</span>
                <span><i className="ri-time-line mr-2"></i>15 min čitanja</span>
                <span><i className="ri-user-line mr-2"></i>BuyInZagreb Stručni tim</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl mb-12">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20new%20construction%20apartment%20building%20Zagreb%20Croatia%2C%20contemporary%20architecture%2C%20glass%20facade%2C%20luxury%20residential%20development%2C%20urban%20skyline%2C%20professional%20real%20estate%20photography%2C%20bright%20daylight&width=1200&height=675&seq=blog-new-construction-main&orientation=landscape"
                alt="Novogradnja vs adaptacija Zagreb"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Ključne razlike između novogradnje i adaptacije</h2>
              
              <p className="text-gray-700 leading-relaxed mb-8">
                Kupnja nekretnine u Zagrebu predstavlja jednu od najvažnijih životnih odluka, a izbor između novogradnje i adaptacije starije nekretnine utjecat će na vaš kvalitetu života, financije i buduću vrijednost investicije. Agencije za nekretnine Zagreb svakodnevno pomažu klijentima u ovom kompleksnom procesu odlučivanja.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Tržište nekretnina Zagreb nudi raznolike mogućnosti - od luksuznih novograđenih apartmana u centru grada do šarmantnih starogradskih stanova s autentičnom arhitekturom koji čekaju svoju transformaciju. Svaka opcija ima svoje prednosti i izazove koje treba pažljivo razmotriti.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Novogradnja - Prednosti modernog pristupa</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Novograđene nekretnine Zagreb pružaju brojne prednosti koje posebno cijene mlađe obitelji i profesionalci. Moderni stanovi dolaze s najsuvremenijom infrastrukturom, energetski učinkovitim sustavima grijanja i hlađenja te suvremenim dizajnom koji odgovara današnjem načinu života.
              </p>

              <div className="bg-green-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-green-600 mb-4">Ključne prednosti novogradnje:</h3>
                <ul className="space-y-3">
                  {[
                    'Energetska učinkovitost - razred A ili B prema EU standardima',
                    'Moderna tehnologija - pametni sustavi upravljanja',
                    'Minimalno održavanje prvih 5-10 godina',
                    'Garancija na instalacije i građevinske elemente',
                    'Mogućnost prilagodbe tijekom gradnje',
                    'Parking mjesta i dodatni sadržaji (fitness, dječje igralište)',
                    'Compliance s najnovijim seizmičkim standardima'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-700 leading-relaxed mb-8">
                Agencije za nekretnine Zagreb ističu da novogradnja donosi predvidljivost troškova. Kupci znaju točno što dobivaju, a garancije na instalacije i strukturu omogućuju mirno uživanje u novom domu bez iznenađenja s održavanjem.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Nedostaci novogradnje koje treba razmotriti</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Unatoč brojnim prednostima, novogradnja ima i svoje nedostatke koje potencijalni kupci trebaju uzeti u obzir. Najviša cijena po kvadratnom metru često je glavni faktor koji odvraća kupce od novograđenih stanova Zagreb.
              </p>

              <div className="bg-red-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Mogući nedostaci novogradnje:</h3>
                <ul className="space-y-3">
                  {[
                    'Visoka cijena - 20-40% iznad prosjeka tržišta nekretnina Zagreb',
                    'Nedostatak karaktera i autentičnosti starih gradnji',
                    'Rizik od kašnjenja u gradnji ili promjena u projektu',
                    'Manje prostora - optimizacija za maksimalnu profitabilnost',
                    'Potencijalni problemi s neiskusnim investitorima',
                    'Standardizirani dizajn bez osobnosti',
                    'Susjedstvo još u izgradnji - buka i prašina'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-alert-line text-red-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Adaptacija starogradnje - Šarm s izazovima</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Zagrebačka starogradnja nudi jedinstvenu priliku za kreiranje personalzirane nekretnine koja odražava vaš stil i potrebe. Mnogi stanovi Zagreb iz perioda austrougarske gradnje imaju visoke stropove, velike prozore i šarm koji se ne može replicirati u modernoj gradnji.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Adaptacija omogućuje kompletnu prilagodbu prostora vašim potrebama - od uklanjanja pregrada do kreiranja open-space koncepta. Agencije za nekretnine Zagreb često rade s arhitektima i izvođačima koji specijalizirano rade na obnovi historijskih zgrada.
              </p>

              <div className="bg-blue-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Prednosti adaptacije starogradnje:</h3>
                <ul className="space-y-3">
                  {[
                    'Niža cijena kupnje - 30-50% manje od novogradnje',
                    'Jedinstvena arhitektura i karakter zgrade',
                    'Mogućnost potpune personalizacije prostora',
                    'Etablirani kvartovi s razvijenom infrastrukturom',
                    'Veći prostori - sobe i hodnici prostranije od moderne gradnje',
                    'Investicijski potencijal - značajan rast vrijednosti nakon renovacije',
                    'Lokacije u centru grada nedostupne novogradnji'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-star-line text-blue-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Troškovi adaptacije - Realifica planiranja</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Adaptacija starogradnje zahtijeva pažljivo planiranje budžeta jer često dolazi do nepredviđenih troškova tijekom renovacije. Stare instalacije mogu zahtijevati kompletnu zamjenu, a strukturalni problemi mogu se otkriti tek početkom radova.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Osnovni troškovi adaptacije:</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Projektna dokumentacija: 50-100 €/m²</li>
                    <li>• Osnovne instalacije: 200-300 €/m²</li>
                    <li>• Podovi i zidovi: 100-200 €/m²</li>
                    <li>• Kuhinja i kupaonice: 300-600 €/m²</li>
                    <li>• Vanjski radovi: 50-150 €/m²</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-yellow-600 mb-4">Dodatni troškovi:</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Nepredviđeni strukturalni radovi: +20-40%</li>
                    <li>• Dozvole i certifikati: 2.000-5.000 €</li>
                    <li>• Energetska certifikacija: 500-800 €</li>
                    <li>• Koordinacija s upravom zgrade</li>
                    <li>• Privremeni smještaj tijekom radova</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mb-8">
                Agencije za nekretnine Zagreb preporučuju rezervaciju dodatnih 25-30% budžeta za nepredviđene situacije. Kvalitetna procjena stanja nekretnine prije kupnje može minimizirati iznenađenja tijekom adaptacije.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Vremeski okviri - Planiranje projekta</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Vremenski okvir adaptacije značajno varira ovisno o opsegu radova i složenosti projekta. Osnovna renovacija stana od 60m² može se završiti za 2-3 mjeseca, dok kompletna adaptacija s promjenom rasporede prostora može potrajati 6-12 mjeseci.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Za razliku od novogradnje gdje su rokovi uglavnom fiksirani, adaptacija omogućuje fleksibilnost u fazama radova. Možete započeti s osnovnim radovima i postupno dodavati elemente prema dostupnosti sredstava i potrebama.
              </p>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-purple-600 mb-4">Tipičan tijek adaptacije:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Faza 1: Priprema (1-2 mjeseca)</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Projektna dokumentacija</li>
                      <li>• Ishođenje dozvola</li>
                      <li>• Odabir izvođača</li>
                      <li>• Nabava materijala</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Faza 2: Izvedba (2-8 mjeseci)</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Rušenje i građevinski radovi</li>
                      <li>• Instalacije (voda, struja, plin)</li>
                      <li>• Završni radovi (podovi, zidovi)</li>
                      <li>• Završno uređenje</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Energetska učinkovitost - Moderni standardi</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Jedan od glavnih nedostataka starijih nekretnina Zagreb je niska energetska učinkovitost. Stare zgrade često imaju energetski razred G ili F, što rezultira visokim troškovima grijanja i hlađenja. Modernizacijom instalacija možete postići značajne uštede.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Investicija u energetsku obnovu može se isplatiti kroz državne potpore i dugoročne uštede na režijama. EU fondovi omogućuju sufinanciranje obnove do 60% ukupnih troškova za određene tipove radova.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Pravni aspekti i dozvole</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Adaptacija starogradnje često zahtijeva različite dozvole ovisno o opsegu planiranih radova. Za osnovne renovacije dovoljno je prijava radova, dok strukturalne promjene zahtijevaju građevinske dozvole koje proces mogu produžiti za nekoliko mjeseci.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Agencije za nekretnine Zagreb surađuju s iskusnim arhitektima koji poznaju specifičnosti rada na zaštićenim zgradama i mogu avisirati o mogućim ograničenjima prije kupnje. Posebna pozornost potrebna je za zgrade pod zaštitom kulturnih spomenika.
              </p>

              <div className="bg-orange-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-orange-600 mb-4">Česta ograničenja u starogradnji:</h3>
                <ul className="space-y-3">
                  {[
                    'Zabrana promjene fasade u historijskim zonama',
                    'Ograničenja u postavljanju klima uređaja',
                    'Obveza korištenja određenih materijala (npr. prirodni kamen)',
                    'Ograničena mogućnost dogradnje ili promjene krovišta',
                    'Potrebna suglasnost upravne zgrade za veće promjene'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-information-line text-orange-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Dugoročna vrijednost investicije</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Obje opcije mogu biti odličens investicije ovisno o lokaciji, kvaliteti izvedbe i tržišnim trendovima. Novogradnja obično zadržava stabilnu vrijednost kroz prve godine, dok kvalitetno adaptirana starogradnja može postići značajan rast vrijednosti.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Statistike agencija za nekretnine Zagreb pokazuju da se dobro adaptiran stan u centru grada može revalorizirati 40-60% u prvih pet godina nakon renovacije, što je značajno više od tipičnog rasta tržišta nekretnina Zagreb od 8-12% godišnje.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kako donijeti pravu odluku?</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Odluka između novogradnje i adaptacije ovisi o vašem životnom stilu, financijskim mogućnostima i dugoročnim planovima. Mlađe obitelji s ograničenim vremenom često biraju novogradnju zbog praktičnosti, dok kreativnije osobe preferiraju jedinstvenos adaptacije.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Ključ uspješne odluke leži u realnoj procjeni vlastitih mogućnosti - kako financijskih tako i vremenskih. Agencije za nekretnine Zagreb mogu organizirati detaljne preglede obje vrste nekretnina i pomoći u procjeni ukupnih troškova i potencijala svake opcije.
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-gray-50 rounded-3xl p-12 text-center mt-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Trebate pomoć u odabiru idealne nekretnine?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Naš tim stručnjaka za nekretnine Zagreb pomoći će vam analizirati sve opcije i pronaći savršeno rješenje za vaše potrebe i budžet. Organiziramo preglede, procjene troškova adaptacije i povezujemo s provjerenim izvođačima.
              </p>
              
              <form 
                onSubmit={handleSubmit}
                className="max-w-md mx-auto space-y-4 mb-8"
                data-readdy-form="blog-new-vs-renovation"
              >
                <input 
                  type="text"
                  name="name"
                  placeholder="Ime i prezime"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <input 
                  type="email"
                  name="email"
                  placeholder="Email adresa"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <select 
                  name="preference"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors pr-8"
                  required
                >
                  <option value="">Vaša preferencija</option>
                  <option value="new-construction">Novogradnja</option>
                  <option value="renovation">Adaptacija</option>
                  <option value="both">Razmatram obje opcije</option>
                  <option value="unsure">Nisam siguran</option>
                </select>
                <input 
                  type="text"
                  name="budget"
                  placeholder="Planirani budžet (EUR)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <textarea 
                  name="requirements"
                  placeholder="Opišite vaše potrebe i zahtjeve za nekretninu..."
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors h-32 resize-none"
                  required
                ></textarea>
                
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-red-600 text-white py-4 rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Šalje se...' : 'Zatraži stručno savjetovanje'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl text-center">
                    Hvala! Naš agent za nekretnine javit će vam se u najkraćem roku.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-center">
                    Dogodila se greška. Molimo pokušajte ponovo.
                  </div>
                )}
              </form>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-home-4-line mr-3"></i>
                  Pogledaj nekretnine
                </button>
                <a 
                  href="/"
                  className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-arrow-left-line mr-3"></i>
                  Povratak na početnu
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      {/* Footer - Simplified */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4">BuyInZagreb</h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Vodeća agencija za nekretnine u Zagrebu s više od 10 godina iskustva u prodaji stanova i kuća.
          </p>
          <div className="flex justify-center items-center gap-6">
            <a href="/#contact" className="text-gray-300 hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}